import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  Paper,
  Button,
  Grid,
  Divider,
  Chip,
  CircularProgress,
  Alert,
  Fade,
  useTheme,
  Card,
  CardContent,
  Avatar,
  Collapse,
  IconButton,
  Tooltip,
  Zoom,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { animated, useSpring } from '@react-spring/web';
import {
  DirectionsCar as CarIcon,
  ArrowBack as BackIcon,
  Edit as EditIcon,
  AttachMoney as MoneyIcon,
  DateRange as DateIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Description as DescriptionIcon,
  AccountBalance as BankIcon,
  Receipt as ReceiptIcon,
  AccountBalanceWallet as WalletIcon,
  Smartphone as SmartphoneIcon,
  CreditCard as CardIcon,
  EventNote as EventNoteIcon,
  Paid as PaidIcon,
  AttachMoney,
  KeyboardArrowDown as ExpandMoreIcon,
  KeyboardArrowUp as ExpandLessIcon,
  Undo as UndoIcon,
  InfoOutlined as InfoIcon,
  Article as ContractIcon,
  InsertDriveFile as FileIcon,
  OpenInNew as OpenInNewIcon,
  PictureAsPdf as PdfIcon,
  Image as ImageIcon,
} from '@mui/icons-material';
import { addMonths, format, isBefore, parseISO, startOfDay } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  Sale,
  SaleDocument,
  SaleInstallment,
  PaymentMethod,
  PaymentStatus,
  PAYMENT_METHODS,
  PAYMENT_STATUS,
  SALE_COMMISSION_TYPE_LABELS,
} from '../../types';
import saleService from '../../services/saleService';
import { useAuthUser } from '../../contexts/AuthUserContext';
import vehicleService from '../../services/vehicleService';
import { isConsignmentCommissionFixed } from '../../utils/consignmentCommission';
import {
  getCashRecognizedProfitFromSale,
  getTermSaleProfitBreakdown,
} from '../../utils/saleRealProfit';

/** Valores das parcelas a prazo para exibição (array, JSON ou valor único legado). */
const getDisplayInstallmentAmounts = (sale: Sale): number[] => {
  const count = Math.max(0, Number(sale.installment_count) || 0);
  const raw = sale.installment_amounts;
  let amounts: number[] = [];
  if (Array.isArray(raw)) {
    amounts = raw.map((x) => Number(x));
  } else if (typeof raw === 'string' && raw.trim()) {
    try {
      const p = JSON.parse(raw);
      if (Array.isArray(p)) amounts = p.map((x) => Number(x));
    } catch {
      amounts = [];
    }
  }
  if (amounts.length === 0 && count > 0) {
    const single = Number(sale.installment_amount);
    if (Number.isFinite(single) && single > 0) {
      amounts = Array(count).fill(single);
    }
  }
  if (count > 0 && amounts.length < count) {
    const pad = amounts[amounts.length - 1] ?? 0;
    while (amounts.length < count) amounts.push(pad);
  } else if (amounts.length > count) {
    amounts = amounts.slice(0, count);
  }
  return amounts;
};

/** Pré-visualização mensal quando a API ainda não devolveu parcelas persistidas (ids negativos). */
const buildPreviewSaleInstallments = (sale: Sale): SaleInstallment[] => {
  const amounts = getDisplayInstallmentAmounts(sale);
  const first = sale.next_installment_due_date;
  if (!first || amounts.length === 0) return [];
  const baseStr = first.split('T')[0];
  const base = parseISO(`${baseStr}T12:00:00`);
  return amounts.map((amount, i) => ({
    id: -(i + 1),
    sale_id: sale.id,
    sequence: i + 1,
    due_date: format(addMonths(base, i), 'yyyy-MM-dd'),
    amount,
    status: 'pending' as PaymentStatus,
    paid_at: null,
  }));
};

const isInstallmentOverdue = (dueDate: string, status: PaymentStatus): boolean => {
  if (status === 'paid') return false;
  try {
    const d = parseISO(`${dueDate.split('T')[0]}T12:00:00`);
    return isBefore(d, startOfDay(new Date()));
  } catch {
    return false;
  }
};

const API_BASE = process.env.REACT_APP_API_URL || '';

const getDocumentFullUrl = (url: string): string => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  const base = API_BASE.replace(/\/$/, '');
  return base ? `${base}${url.startsWith('/') ? url : `/${url}`}` : url;
};

const getDocumentIcon = (url: string, type?: string) => {
  const lower = (url || '').toLowerCase();
  const t = (type || '').toLowerCase();
  if (lower.includes('.pdf') || t === 'pdf') return 'pdf';
  if (lower.match(/\.(jpe?g|png|gif|webp)$/) || t === 'imagem' || t === 'foto') return 'image';
  return 'file';
};

// Componente para ícone do método de pagamento
const PaymentMethodIcon = ({ method }: { method: PaymentMethod }) => {
  switch (method) {
    case 'money':
      return <WalletIcon />;
    case 'pix':
      return <SmartphoneIcon />;
    case 'check':
      return <ReceiptIcon />;
    case 'financing':
      return <BankIcon />;
    case 'term':
      return <EventNoteIcon />;
    case 'bank_transfer':
      return <BankIcon />;
    case 'other':
    default:
      return <CardIcon />;
  }
};

// Componente para exibir o status do pagamento
const PaymentStatusChip = ({ status }: { status: PaymentStatus }) => {
  const getStatusColor = (status: PaymentStatus) => {
    switch (status) {
      case 'paid':
        return 'success';
      case 'partial':
        return 'warning';
      case 'pending':
      default:
        return 'error';
    }
  };

  return (
    <Chip
      label={PAYMENT_STATUS[status]}
      color={getStatusColor(status)}
      size="medium"
      icon={<PaidIcon />}
      sx={{ fontWeight: 'bold' }}
    />
  );
};

const AnimatedCard = animated(Card);

const SaleDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const { canWrite } = useAuthUser();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sale, setSale] = useState<Sale | null>(null);
  const [profit, setProfit] = useState<number | null>(null);
  const [totalExpense, setTotalExpense] = useState<number | null>(null);
  const [isConsignado, setIsConsignado] = useState<boolean>(false);
  const [commissionValue, setCommissionValue] = useState<number | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [termSchedule, setTermSchedule] = useState<{
    rows: SaleInstallment[];
    isPreview: boolean;
  } | null>(null);
  const [termInstallmentsLoading, setTermInstallmentsLoading] = useState(false);
  const [termActionId, setTermActionId] = useState<number | null>(null);
  const [termError, setTermError] = useState<string | null>(null);

  const applyDerivedFromSale = useCallback((data: Sale) => {
    const totalExpenses = data.vehicle?.expenses?.reduce((sum, expense) => sum + expense.amount, 0) ?? 0;
    setTotalExpense(totalExpenses);

    const vehicle = data.vehicle;

    if (vehicle?.is_consignado && vehicle?.consignment_commission != null) {
      setIsConsignado(true);
      const commission =
        isConsignmentCommissionFixed(vehicle.consignment_commission_type)
          ? Number(vehicle.consignment_commission)
          : data.sale_price * (Number(vehicle.consignment_commission) / 100);
      setCommissionValue(commission);
    } else {
      setIsConsignado(false);
      setCommissionValue(null);
    }

    setProfit(getCashRecognizedProfitFromSale(data));
  }, []);

  useEffect(() => {
    const fetchSaleDetails = async () => {
      try {
        setLoading(true);
        const data = await saleService.getSale(Number(id));
        setSale(data);
        applyDerivedFromSale(data);
        setLoading(false);
      } catch (error) {
        console.error('Erro ao carregar detalhes da venda:', error);
        setError('Não foi possível carregar os detalhes da venda. Tente novamente mais tarde.');
        setLoading(false);
      }
    };

    fetchSaleDetails();
  }, [id, applyDerivedFromSale]);

  useEffect(() => {
    if (!sale || sale.payment_method !== 'term') {
      setTermSchedule(null);
      setTermInstallmentsLoading(false);
      return;
    }

    let cancelled = false;

    (async () => {
      setTermInstallmentsLoading(true);
      setTermError(null);
      try {
        let rows: SaleInstallment[] = sale.installments?.length ? [...sale.installments] : [];
        if (!rows.length) {
          rows = await saleService.getInstallments(sale.id);
        }
        rows.sort((a, b) => a.sequence - b.sequence);
        if (cancelled) return;
        if (rows.length > 0) {
          setTermSchedule({ rows, isPreview: false });
        } else {
          const preview = buildPreviewSaleInstallments(sale);
          setTermSchedule(preview.length > 0 ? { rows: preview, isPreview: true } : null);
        }
      } catch {
        if (cancelled) return;
        const preview = buildPreviewSaleInstallments(sale);
        setTermSchedule(preview.length > 0 ? { rows: preview, isPreview: true } : null);
      } finally {
        if (!cancelled) setTermInstallmentsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [sale]);

  useEffect(() => {
    if (!sale || sale.payment_method !== 'term') return;
    if (!termSchedule?.rows.length || termSchedule.isPreview) return;
    setProfit(
      getCashRecognizedProfitFromSale({ ...sale, installments: termSchedule.rows })
    );
  }, [sale, termSchedule]);

  const handlePayInstallment = async (installmentId: number) => {
    if (!sale || installmentId <= 0) return;
    setTermActionId(installmentId);
    setTermError(null);
    try {
      await saleService.payInstallment(sale.id, installmentId);
      const data = await saleService.getSale(Number(id));
      setSale(data);
      applyDerivedFromSale(data);
    } catch {
      setTermError(
        'Não foi possível registrar o pagamento. Verifique se a API implementa POST /sales/:id/installments/:id/pay.'
      );
    } finally {
      setTermActionId(null);
    }
  };

  const handleUnpayInstallment = async (installmentId: number) => {
    if (!sale || installmentId <= 0) return;
    setTermActionId(installmentId);
    setTermError(null);
    try {
      await saleService.unpayInstallment(sale.id, installmentId);
      const data = await saleService.getSale(Number(id));
      setSale(data);
      applyDerivedFromSale(data);
    } catch {
      setTermError('Não foi possível desfazer o pagamento (endpoint .../unpay).');
    } finally {
      setTermActionId(null);
    }
  };

  const termProfitBreakdown = useMemo(() => {
    if (!sale || sale.payment_method !== 'term') return null
    const installments = termSchedule?.rows ?? sale.installments ?? []
    const saleForBreakdown =
      termSchedule?.rows?.length && !termSchedule.isPreview
        ? { ...sale, installments: termSchedule.rows }
        : sale
    return getTermSaleProfitBreakdown(saleForBreakdown, installments)
  }, [sale, termSchedule])

  const handleEditSale = () => {
    navigate(`/vendas/editar/${id}`);
  };

  const handleBackToList = () => {
    navigate('/vendas');
  };

  // Formatar valores monetários
  const formatCurrency = (value?: number | null) => {
    if (value === undefined || value === null) {
      return '-';
    }
    return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  // Formatar data
  const formatDate = (dateString: string) => {
    if (!dateString) return '-';

    try {
      const cleanDateString = dateString.split('T')[0];  // Vai pegar apenas '2025-05-01'
      const [year, month, day] = cleanDateString.split('-');
      return `${day}/${month}/${year}`;
    } catch (error) {
      console.error('Erro ao formatar data:', dateString, error);
      return '-';
    }
  };

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  // Animação para o card de lucro
  const cardSpring = useSpring({
    transform: expanded
      ? 'scale(1.03) translateY(-10px)'
      : 'scale(1) translateY(0px)',
    boxShadow: expanded
      ? '0 12px 32px rgba(0, 0, 0, 0.28)'
      : '0 4px 14px rgba(0, 0, 0, 0.15)',
    config: {
      tension: 300,
      friction: 20
    }
  });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
        <CircularProgress size={60} thickness={4} sx={{ color: theme.palette.primary.main }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ mb: 2, borderRadius: '8px' }}>
          {error}
        </Alert>
        <Button startIcon={<BackIcon />} onClick={handleBackToList}>
          Voltar para lista
        </Button>
      </Box>
    );
  }

  if (!sale) {
    return (
      <Box sx={{ mt: 4 }}>
        <Alert severity="warning" sx={{ mb: 2, borderRadius: '8px' }}>
          Venda não encontrada.
        </Alert>
        <Button startIcon={<BackIcon />} onClick={handleBackToList}>
          Voltar para lista
        </Button>
      </Box>
    );
  }

  return (
    <Fade in={true} timeout={800}>
      <div>
        {/* Overlay para quando o card estiver expandido */}
        {expanded && (
          <Box
            onClick={() => setExpanded(false)}
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: 'rgba(0, 0, 0, 0.3)',
              zIndex: 5,
              transition: 'opacity 0.3s ease',
              backdropFilter: 'blur(2px)',
            }}
          />
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
          <Button
            startIcon={<BackIcon />}
            onClick={handleBackToList}
            sx={{ mr: 2 }}
            color="inherit"
          >
            Voltar
          </Button>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', flexGrow: 1 }}>
            Detalhes da Venda
          </Typography>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<ContractIcon />}
            onClick={() => navigate(`/contratos/venda/${id}`)}
            sx={{ borderRadius: '8px', mr: 1 }}
          >
            Contrato de venda
          </Button>
          {canWrite && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<EditIcon />}
              onClick={handleEditSale}
              sx={{ borderRadius: '8px' }}
            >
              Editar
            </Button>
          )}
        </Box>

        <Paper
          sx={{
            p: { xs: 2, md: 4 },
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
            mb: 4,
          }}
        >
          {/* Cabeçalho com informações principais */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 4 }}>
            <Avatar
              sx={{
                bgcolor: theme.palette.primary.main,
                width: 80,
                height: 80,
                mr: 3,
              }}
            >
              <MoneyIcon sx={{ fontSize: 40 }} />
            </Avatar>
            <Box>
              <Typography variant="h4" component="h2" sx={{ fontWeight: 'bold' }}>
                {sale.vehicle?.brand} {sale.vehicle?.model}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <PaymentStatusChip status={sale.payment_status} />
                <Typography variant="body1" color="text.secondary" sx={{ ml: 2 }}>
                  Data: {formatDate(sale.sale_date)}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Informações detalhadas */}
          <Typography variant="h6" component="h3" sx={{ mb: 3 }}>
            Informações da Venda
          </Typography>

          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ borderRadius: '12px', height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                    <MoneyIcon sx={{ mr: 1, fontSize: 20 }} />
                    Valor da Venda
                  </Typography>
                  <Typography variant="h6">{formatCurrency(sale.sale_price)}</Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ borderRadius: '12px', height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                    <DateIcon sx={{ mr: 1, fontSize: 20 }} />
                    Data da Venda
                  </Typography>
                  <Typography variant="h6">{formatDate(sale.sale_date)}</Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ borderRadius: '12px', height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <PaymentMethodIcon method={sale.payment_method} />
                    <Typography variant="subtitle2" color="text.secondary" sx={{ ml: 1 }}>
                      Forma de Pagamento
                    </Typography>
                  </Box>

                  <Typography variant="h6">
                    {PAYMENT_METHODS[sale.payment_method]}
                  </Typography>

                  {/* Detalhes do financiamento */}
                  {sale.payment_method === "financing" && (
                    <Box sx={{ mt: 1.5 }}>
                      {sale.down_payment && (
                        <Typography variant="body2" color="text.secondary">
                          Entrada: {formatCurrency(sale.down_payment)}
                        </Typography>
                      )}

                      {sale.financed_amount && (
                        <Typography variant="body2" color="text.secondary">
                          Financiado: {formatCurrency(sale.financed_amount)}
                        </Typography>
                      )}

                      {sale.bank && (
                        <Typography variant="body2" color="text.secondary">
                          Banco: {sale.bank}
                        </Typography>
                      )}

                      {sale.commission_value && (
                        <Typography variant="body2" color="text.secondary">
                          Comissão banco: {formatCurrency(sale.commission_value)}
                        </Typography>
                      )}
                    </Box>
                  )}

                  {sale.payment_method === 'term' && (
                    <Box sx={{ mt: 1.5 }}>
                      {sale.down_payment != null && Number(sale.down_payment) > 0 && (
                        <Typography variant="body2" color="text.secondary">
                          Entrada: {formatCurrency(Number(sale.down_payment))}
                        </Typography>
                      )}
                      {sale.next_installment_due_date && (
                        <Typography variant="body2" color="text.secondary">
                          Próxima prestação: {formatDate(sale.next_installment_due_date)}
                        </Typography>
                      )}
                      {sale.installment_count != null &&
                        Number(sale.installment_count) >= 1 && (
                          <Typography variant="body2" color="text.secondary">
                            Parcelas (após entrada): {Number(sale.installment_count)}
                          </Typography>
                        )}
                      {getDisplayInstallmentAmounts(sale).map((amt, i) =>
                        amt > 0 ? (
                          <Typography key={i} variant="body2" color="text.secondary">
                            Parcela {i + 1}: {formatCurrency(amt)}
                          </Typography>
                        ) : null
                      )}
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {profit !== null && (
              <Grid item xs={12} sm={6} md={3}>
                <AnimatedCard
                  style={cardSpring}
                  sx={{
                    borderRadius: '12px',
                    height: 'auto',
                    position: 'relative',
                    zIndex: expanded ? 10 : 1,
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: expanded ? '6px' : '4px',
                      bgcolor: profit && profit > 0
                        ? theme.palette.success.main
                        : profit && profit < 0
                          ? theme.palette.error.main
                          : theme.palette.info.main,
                      transition: 'height 0.3s ease'
                    }}
                  />
                  <CardContent
                    sx={{
                      cursor: 'pointer',
                      position: 'relative',
                      transition: 'all 0.3s ease',
                      bgcolor: profit && profit > 0
                        ? theme.palette.mode === 'dark' ? 'rgba(46, 125, 50, 0.2)' : 'rgba(46, 125, 50, 0.1)'
                        : profit && profit < 0
                          ? theme.palette.mode === 'dark' ? 'rgba(211, 47, 47, 0.2)' : 'rgba(211, 47, 47, 0.1)'
                          : theme.palette.mode === 'dark' ? 'rgba(25, 118, 210, 0.2)' : 'rgba(25, 118, 210, 0.1)',
                      pb: expanded ? 0 : 2,
                      '&:hover': {
                        bgcolor: profit && profit > 0
                          ? theme.palette.mode === 'dark' ? 'rgba(46, 125, 50, 0.25)' : 'rgba(46, 125, 50, 0.15)'
                          : profit && profit < 0
                            ? theme.palette.mode === 'dark' ? 'rgba(211, 47, 47, 0.25)' : 'rgba(211, 47, 47, 0.15)'
                            : theme.palette.mode === 'dark' ? 'rgba(25, 118, 210, 0.25)' : 'rgba(25, 118, 210, 0.15)'
                      }
                    }}
                    onClick={handleExpandClick}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          color: profit && profit > 0
                            ? theme.palette.success.main
                            : profit && profit < 0
                              ? theme.palette.error.main
                              : theme.palette.info.main,
                          fontWeight: 'bold',
                          transition: 'transform 0.3s ease',
                          transform: expanded ? 'scale(1.05)' : 'scale(1)'
                        }}
                      >
                        <AttachMoney sx={{
                          mr: 1,
                          fontSize: expanded ? 24 : 20,
                          transition: 'fontSize 0.3s ease'
                        }} />
                        {sale?.payment_method === 'term' ? 'Lucro (caixa)' : 'Lucro'}
                      </Typography>
                      <Zoom in={true}>
                        <Tooltip title={expanded ? "Recolher detalhes" : "Expandir para detalhes"}>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleExpandClick();
                            }}
                            sx={{
                              transition: 'transform 0.3s',
                              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                              color: profit && profit > 0
                                ? theme.palette.success.main
                                : profit && profit < 0
                                  ? theme.palette.error.main
                                  : theme.palette.info.main,
                            }}
                          >
                            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                        </Tooltip>
                      </Zoom>
                    </Box>

                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 'bold',
                        color: profit && profit > 0
                          ? theme.palette.success.main
                          : profit && profit < 0
                            ? theme.palette.error.main
                            : theme.palette.info.main,
                        mb: 1,
                        fontSize: expanded ? '1.5rem' : '1.25rem',
                        transition: 'fontSize 0.3s ease',
                      }}
                    >
                      {formatCurrency(profit)}
                      <Typography
                        component="span"
                        variant="body2"
                        sx={{
                          display: 'block',
                          color: 'text.secondary',
                          fontWeight: 'normal',
                          mt: 0.5
                        }}
                      >
                        {profit > 0
                          ? `(+${Math.round((profit / sale.sale_price) * 100)}%)`
                          : profit < 0
                            ? `(${Math.round((profit / sale.sale_price) * 100)}%)`
                            : '(0%)'}
                      </Typography>
                    </Typography>
                  </CardContent>

                  <Collapse in={expanded} timeout="auto" unmountOnExit>
                    <Divider sx={{ mt: 0, mb: 1.5 }} />

                    <CardContent sx={{ pt: 0, pb: '16px !important' }}>
                      <Box sx={{ mt: 1, opacity: expanded ? 1 : 0, transition: 'opacity 0.6s ease 0.1s' }}>
                        {isConsignado ? (
                          <>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                              <Typography variant="body2" color="text.secondary">Valor da venda:</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 'medium' }}>{formatCurrency(sale.sale_price)}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                              <Typography variant="body2" color="text.secondary">
                                Comissão ({isConsignmentCommissionFixed(sale.vehicle?.consignment_commission_type)
                                  ? 'valor fixo'
                                  : `${sale.vehicle?.consignment_commission ?? 0}%`}):
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                {commissionValue !== null ? formatCurrency(commissionValue) : '-'}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                              <Typography variant="body2" color="text.secondary">Despesas:</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                {totalExpense != null ? formatCurrency(totalExpense) : '-'}
                              </Typography>
                            </Box>
                            <Divider sx={{ my: 1 }} />
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold' }}>Lucro (comissão − despesas):</Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 'bold',
                                  color: profit != null && profit > 0 ? theme.palette.success.main : profit != null && profit < 0 ? theme.palette.error.main : theme.palette.info.main,
                                }}
                              >
                                {profit != null ? formatCurrency(profit) : '-'}
                              </Typography>
                            </Box>
                            {(() => {
                              const sellerComm = sale.commission_amount ?? (sale.commission_type && sale.commission_value != null && profit != null
                                ? sale.commission_type === 'fixed' ? Number(sale.commission_value)
                                  : sale.commission_type === 'percent_sale' ? sale.sale_price * (Number(sale.commission_value) / 100)
                                    : Math.max(0, profit) * (Number(sale.commission_value) / 100)
                                : 0);
                              if (sellerComm <= 0) return null;
                              const netProfit = (profit ?? 0) - sellerComm;
                              return (
                                <>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, mt: 1 }}>
                                    <Typography variant="body2" color="text.secondary">Comissão do vendedor (saída):</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 'medium', color: theme.palette.error.main }}>{formatCurrency(sellerComm)}</Typography>
                                  </Box>
                                  <Divider sx={{ my: 1 }} />
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold' }}>Lucro líquido (lucro − comissão):</Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontWeight: 'bold',
                                        color: netProfit > 0 ? theme.palette.success.main : netProfit < 0 ? theme.palette.error.main : theme.palette.info.main,
                                      }}
                                    >
                                      {formatCurrency(netProfit)}
                                    </Typography>
                                  </Box>
                                </>
                              );
                            })()}
                          </>
                        ) : (
                          <>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                              <Typography variant="body2" color="text.secondary">Preço de venda:</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 'medium' }}>{formatCurrency(sale.sale_price)}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                              <Typography variant="body2" color="text.secondary">Preço de compra:</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                {sale?.vehicle?.purchase_price != null ? formatCurrency(sale.vehicle.purchase_price) : '-'}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                              <Typography variant="body2" color="text.secondary">Despesas:</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                {totalExpense != null ? formatCurrency(totalExpense) : '-'}
                              </Typography>
                            </Box>
                            <Divider sx={{ my: 1 }} />
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold' }}>Lucro (venda − compra − despesas):</Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 'bold',
                                  color: profit != null && profit > 0 ? theme.palette.success.main : profit != null && profit < 0 ? theme.palette.error.main : theme.palette.info.main,
                                }}
                              >
                                {profit != null ? formatCurrency(profit) : '-'}
                              </Typography>
                            </Box>
                            {(() => {
                              const sellerComm = sale.commission_amount ?? (sale.commission_type && sale.commission_value != null && profit != null
                                ? sale.commission_type === 'fixed' ? Number(sale.commission_value)
                                  : sale.commission_type === 'percent_sale' ? sale.sale_price * (Number(sale.commission_value) / 100)
                                    : Math.max(0, profit) * (Number(sale.commission_value) / 100)
                                : 0);
                              if (sellerComm <= 0) return null;
                              const netProfit = (profit ?? 0) - sellerComm;
                              return (
                                <>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, mt: 1 }}>
                                    <Typography variant="body2" color="text.secondary">Comissão do vendedor (saída):</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 'medium', color: theme.palette.error.main }}>{formatCurrency(sellerComm)}</Typography>
                                  </Box>
                                  <Divider sx={{ my: 1 }} />
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold' }}>Lucro líquido (lucro − comissão):</Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontWeight: 'bold',
                                        color: netProfit > 0 ? theme.palette.success.main : netProfit < 0 ? theme.palette.error.main : theme.palette.info.main,
                                      }}
                                    >
                                      {formatCurrency(netProfit)}
                                    </Typography>
                                  </Box>
                                </>
                              );
                            })()}
                          </>
                        )}
                      </Box>
                    </CardContent>
                  </Collapse>
                </AnimatedCard>
              </Grid>
            )}
          </Grid>

          {sale.payment_method === 'term' && (
            <>
              <Divider sx={{ my: 3 }} />
              <Typography variant="h6" component="h3" sx={{ mb: 1.5 }}>
                Parcelas da venda (pagamento a prazo)
              </Typography>
              {termError && (
                <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setTermError(null)}>
                  {termError}
                </Alert>
              )}
              {termInstallmentsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress size={36} />
                </Box>
              ) : termSchedule?.rows?.length ? (
                <>
                  {termSchedule.isPreview && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Visualização estimada (vencimentos mensais a partir da primeira data). Para registrar pagamentos
                      aqui, o backend deve criar parcelas ao salvar a venda ou expor GET /sales/:id/installments.
                    </Alert>
                  )}
                  <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, mb: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell width={72}>Nº</TableCell>
                          <TableCell>Vencimento</TableCell>
                          <TableCell align="right">Valor</TableCell>
                          <TableCell align="right">Lucro (caixa)</TableCell>
                          <TableCell>Situação</TableCell>
                          <TableCell>Pago em</TableCell>
                          <TableCell align="right">Ações</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {termSchedule.rows.map((row) => {
                          const overdue =
                            row.status !== 'paid' && isInstallmentOverdue(row.due_date, row.status);
                          const actionable = row.id > 0 && !termSchedule.isPreview;
                          const busy = termActionId === row.id;
                          const profitRow = termProfitBreakdown?.rows.find(
                            (r) => r.key === `installment-${row.id}`
                          );
                          const installmentPaid =
                            sale.payment_status === 'paid' || row.status === 'paid';
                          return (
                            <TableRow key={row.id} hover>
                              <TableCell>{row.sequence}</TableCell>
                              <TableCell>{formatDate(row.due_date)}</TableCell>
                              <TableCell align="right">{formatCurrency(row.amount)}</TableCell>
                              <TableCell align="right">
                                {profitRow ? (
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontWeight: 600,
                                      color: installmentPaid
                                        ? theme.palette.success.main
                                        : 'text.secondary',
                                    }}
                                  >
                                    {installmentPaid
                                      ? formatCurrency(profitRow.profitAmount)
                                      : `(${formatCurrency(profitRow.profitAmount)})`}
                                  </Typography>
                                ) : (
                                  '—'
                                )}
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                                  <Chip
                                    size="small"
                                    label={PAYMENT_STATUS[row.status]}
                                    color={
                                      row.status === 'paid'
                                        ? 'success'
                                        : row.status === 'partial'
                                          ? 'warning'
                                          : 'default'
                                    }
                                    variant="outlined"
                                  />
                                  {overdue && (
                                    <Chip size="small" label="Atrasada" color="error" variant="outlined" />
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell>{row.paid_at ? formatDate(row.paid_at) : '—'}</TableCell>
                              <TableCell align="right">
                                {actionable && row.status !== 'paid' && canWrite && (
                                  <Button
                                    size="small"
                                    variant="contained"
                                    disabled={busy}
                                    onClick={() => handlePayInstallment(row.id)}
                                  >
                                    {busy ? '…' : 'Registrar pagamento'}
                                  </Button>
                                )}
                                {actionable && row.status === 'paid' && canWrite && (
                                  <Tooltip title="Volta a parcela para pendente">
                                    <span>
                                      <Button
                                        size="small"
                                        color="inherit"
                                        startIcon={<UndoIcon />}
                                        disabled={busy}
                                        onClick={() => handleUnpayInstallment(row.id)}
                                      >
                                        Desfazer
                                      </Button>
                                    </span>
                                  </Tooltip>
                                )}
                                {!actionable && (
                                  <Typography variant="caption" color="text.secondary">
                                    —
                                  </Typography>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Nenhuma parcela para exibir. Informe valores e vencimento na edição da venda ou aguarde a API gerar as
                  parcelas.
                </Typography>
              )}

              {!termInstallmentsLoading && termProfitBreakdown && termProfitBreakdown.rows.length > 0 && (
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
                    Discriminação do lucro no caixa
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Cada pagamento reconhece lucro proporcional ao valor recebido. Valores entre parênteses ainda não
                    entraram no caixa.
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Pagamento</TableCell>
                          <TableCell align="right">Valor</TableCell>
                          <TableCell align="right">Lucro no caixa</TableCell>
                          <TableCell>Situação</TableCell>
                          <TableCell>Data</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {termProfitBreakdown.rows.map((row) => (
                          <TableRow key={row.key}>
                            <TableCell>{row.label}</TableCell>
                            <TableCell align="right">{formatCurrency(row.paymentAmount)}</TableCell>
                            <TableCell align="right">
                              <Typography
                                component="span"
                                variant="body2"
                                sx={{
                                  fontWeight: 600,
                                  color:
                                    row.status === 'paid'
                                      ? theme.palette.success.main
                                      : 'text.secondary',
                                }}
                              >
                                {row.status === 'paid'
                                  ? formatCurrency(row.profitAmount)
                                  : `(${formatCurrency(row.profitAmount)})`}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={row.status === 'paid' ? 'No caixa' : 'Pendente'}
                                color={row.status === 'paid' ? 'success' : 'default'}
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>
                              {row.status === 'paid' && row.paidAt
                                ? formatDate(row.paidAt)
                                : row.dueDate
                                  ? `Vence ${formatDate(row.dueDate)}`
                                  : '—'}
                            </TableCell>
                          </TableRow>
                        ))}
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700 }}>Lucro total da operação</TableCell>
                          <TableCell />
                          <TableCell align="right" sx={{ fontWeight: 700 }}>
                            {formatCurrency(termProfitBreakdown.totalProfit)}
                          </TableCell>
                          <TableCell colSpan={2} />
                        </TableRow>
                        <TableRow sx={{ bgcolor: 'action.hover' }}>
                          <TableCell sx={{ fontWeight: 700, color: 'success.main' }}>
                            Já reconhecido no caixa
                          </TableCell>
                          <TableCell />
                          <TableCell align="right" sx={{ fontWeight: 700, color: 'success.main' }}>
                            {formatCurrency(termProfitBreakdown.recognizedProfit)}
                          </TableCell>
                          <TableCell colSpan={2} />
                        </TableRow>
                        <TableRow sx={{ bgcolor: 'action.hover' }}>
                          <TableCell sx={{ fontWeight: 700, color: 'text.secondary' }}>
                            Ainda a reconhecer
                          </TableCell>
                          <TableCell />
                          <TableCell align="right" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                            {formatCurrency(termProfitBreakdown.pendingProfit)}
                          </TableCell>
                          <TableCell colSpan={2} />
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              )}
            </>
          )}

          {(sale.seller_id != null || sale.seller != null || (sale.commission_type != null && sale.commission_value != null)) && (
            <>
              <Divider sx={{ my: 3 }} />
              <Typography variant="h6" component="h3" sx={{ mb: 2 }}>
                Informações do vendedor
              </Typography>
              <Grid container spacing={3} sx={{ mb: 4 }}>
                {(sale.seller?.name != null || sale.seller_id != null) && (
                  <Grid item xs={12} sm={6} md={4}>
                    <Card sx={{ borderRadius: '12px', height: '100%' }}>
                      <CardContent>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                          Responsável pela venda
                        </Typography>
                        <Typography variant="h6">{sale.seller?.name ?? `ID ${sale.seller_id}`}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
                {sale.commission_type != null && sale.commission_value != null && (
                  <>
                    <Grid item xs={12} sm={6} md={4}>
                      <Card sx={{ borderRadius: '12px', height: '100%' }}>
                        <CardContent>
                          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                            Tipo de comissão
                          </Typography>
                          <Typography variant="h6">{SALE_COMMISSION_TYPE_LABELS[sale.commission_type] ?? sale.commission_type}</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Card sx={{ borderRadius: '12px', height: '100%' }}>
                        <CardContent>
                          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                            Comissão
                          </Typography>
                          <Typography variant="h6">
                            {sale.commission_type === 'fixed'
                              ? formatCurrency(sale.commission_value)
                              : `${Number(sale.commission_value)}%`}
                            {sale.commission_amount != null && sale.commission_amount > 0 && (
                              <Typography component="span" variant="body2" color="text.secondary" display="block">
                                Valor: {formatCurrency(sale.commission_amount)}
                              </Typography>
                            )}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </>
                )}
              </Grid>
            </>
          )}

          <Divider sx={{ my: 3 }} />

          {/* Informações do Cliente */}
          <Typography variant="h6" component="h3" sx={{ mb: 3 }}>
            Informações do cliente
          </Typography>

          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{ borderRadius: '12px', height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                    <PersonIcon sx={{ mr: 1, fontSize: 20 }} />
                    Nome
                  </Typography>
                  <Typography variant="h6">{sale.buyer_name}</Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{ borderRadius: '12px', height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                    <PhoneIcon sx={{ mr: 1, fontSize: 20 }} />
                    Telefone
                  </Typography>
                  <Typography variant="h6">{sale.buyer_phone || '-'}</Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{ borderRadius: '12px', height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                    <DescriptionIcon sx={{ mr: 1, fontSize: 20 }} />
                    CPF/CNPJ
                  </Typography>
                  <Typography variant="h6">{sale.buyer_document || '-'}</Typography>
                </CardContent>
              </Card>
            </Grid>

            {(sale.buyer_email != null && sale.buyer_email !== '') && (
              <Grid item xs={12} sm={6} md={4}>
                <Card sx={{ borderRadius: '12px', height: '100%' }}>
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                      E-mail
                    </Typography>
                    <Typography variant="h6">{sale.buyer_email}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {(sale.buyer_address != null && sale.buyer_address !== '') && (
              <Grid item xs={12} sm={6} md={4}>
                <Card sx={{ borderRadius: '12px', height: '100%' }}>
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                      Endereço
                    </Typography>
                    <Typography variant="h6">{sale.buyer_address}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {(sale.buyer_city != null || sale.buyer_state != null || sale.buyer_zip != null) && (
              <Grid item xs={12} sm={6} md={4}>
                <Card sx={{ borderRadius: '12px', height: '100%' }}>
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                      Cidade / Estado / CEP
                    </Typography>
                    <Typography variant="h6">
                      {[sale.buyer_city, sale.buyer_state, sale.buyer_zip].filter(Boolean).join(' — ') || '-'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>

          {/* Documentos anexados */}
          {sale.documents && sale.documents.length > 0 && (
            <>
              <Divider sx={{ my: 3 }} />
              <Typography variant="h6" component="h3" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                <FileIcon /> Documentos anexados
              </Typography>
              <Grid container spacing={2} sx={{ mb: 4 }}>
                {(sale.documents as SaleDocument[]).map((doc) => {
                  const fullUrl = getDocumentFullUrl(doc.url);
                  const iconType = getDocumentIcon(doc.url, doc.type);
                  const label = doc.type ? String(doc.type).replace(/_/g, ' ') : (doc.file_name || 'Documento');
                  return (
                    <Grid item xs={12} sm={6} md={4} key={doc.id}>
                      <Card
                        sx={{
                          borderRadius: '12px',
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          overflow: 'hidden',
                          transition: 'box-shadow 0.2s',
                          '&:hover': { boxShadow: 4 },
                        }}
                      >
                        <Box
                          sx={{
                            height: 120,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                          }}
                        >
                          {iconType === 'pdf' && (
                            <PdfIcon sx={{ fontSize: 56, color: theme.palette.error.main }} />
                          )}
                          {iconType === 'image' && (
                            <ImageIcon sx={{ fontSize: 56, color: theme.palette.info.main }} />
                          )}
                          {iconType === 'file' && (
                            <FileIcon sx={{ fontSize: 56, color: theme.palette.primary.main }} />
                          )}
                        </Box>
                        <CardContent sx={{ flex: 1, pt: 1.5, pb: 1.5 }}>
                          <Typography variant="subtitle2" noWrap title={label} sx={{ fontWeight: 600, mb: 1 }}>
                            {label}
                          </Typography>
                          <Button
                            component="a"
                            href={fullUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            size="small"
                            variant="outlined"
                            fullWidth
                            startIcon={<OpenInNewIcon />}
                          >
                            Abrir documento
                          </Button>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </>
          )}

          {/* Observações */}
          {sale.notes && (
            <>
              <Divider sx={{ my: 3 }} />
              <Typography variant="h6" component="h3" sx={{ mb: 2 }}>
                Observações
              </Typography>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: '8px',
                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
                  mb: 3
                }}
              >
                <Typography variant="body1">
                  {sale.notes}
                </Typography>
              </Paper>
            </>
          )}

          {/* Informações do Veículo */}
          {sale.vehicle && (
            <>
              <Divider sx={{ my: 3 }} />
              <Typography variant="h6" component="h3" sx={{ mb: 3 }}>
                Informações do Veículo
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ borderRadius: '12px', height: '100%' }}>
                    <CardContent>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                        <CarIcon sx={{ mr: 1, fontSize: 20 }} />
                        Marca/Modelo
                      </Typography>
                      <Typography variant="h6">
                        {sale.vehicle.brand} {sale.vehicle.model}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ borderRadius: '12px', height: '100%' }}>
                    <CardContent>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                        <DateIcon sx={{ mr: 1, fontSize: 20 }} />
                        Ano
                      </Typography>
                      <Typography variant="h6">{sale.vehicle.year}</Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ borderRadius: '12px', height: '100%' }}>
                    <CardContent>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                        <MoneyIcon sx={{ mr: 1, fontSize: 20 }} />
                        Preço de Compra
                      </Typography>
                      <Typography variant="h6">
                        {formatCurrency(sale.vehicle.purchase_price)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ borderRadius: '12px', height: '100%' }}>
                    <CardContent>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                        <DescriptionIcon sx={{ mr: 1, fontSize: 20 }} />
                        Placa
                      </Typography>
                      <Typography variant="h6">{sale.vehicle.plate}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </>
          )}
        </Paper>
      </div>
    </Fade>
  );
};

export default SaleDetails; 